import React, { useState } from "react";
import { FaTimes } from "react-icons/fa";
import { AiOutlineLoading } from "react-icons/ai";
import { useGenerateContentContext } from "../../../context/GenerateContentContext";

const GenerateImage = ({
  setShowImageModal,
  setImage, // kept for compatibility if used elsewhere
  setThumbnail,
  setThumbnailId,
}: any) => {
  const [prompt, setPrompt] = useState("");
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState("");
  const [allGeneratedImages, setAllGeneratedImages] = useState([]);

  const { generateImage } = useGenerateContentContext();

  const sendPrompt = async () => {
    if (!prompt || prompt === "") setIsError(true);

    setLoading(true);
    setIsError(false);

    const response = await generateImage({ data: { message: prompt } });

    setLoading(false);

    if (response) {
      setThumbnail(response?.image_kit_url);
      setThumbnailId(response?.image_kit_file_id);
      setSelected(response?.image_kit_file_id);

      const images =
        allGeneratedImages.length > 0 ? allGeneratedImages.map((img) => img) : [];

      images.push({
        image_url: response?.image_kit_url,
        image_id: response?.image_kit_file_id,
      });

      setAllGeneratedImages(images);
    }
  };

  const btnBase =
    "inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2";
  const btnPrimary =
    "bg-blue-600 text-white border-blue-600 hover:bg-blue-500 focus-visible:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed";
  const btnNeutral =
    "bg-white text-slate-700 border-slate-200 hover:bg-slate-50";

  return (
    <div className="fixed inset-0 z-[2147483647]">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />
      <div className="absolute inset-0 flex items-start justify-center p-4 sm:p-6 overflow-y-auto">
        <div className="relative w-full max-w-3xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden my-auto">
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b bg-white/95 backdrop-blur">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Generate Image</h3>
              <p className="mt-0.5 text-[11px] text-slate-500">
                Describe the image you’d like to create. Keep it specific and visual.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowImageModal(false)}
              className={`${btnBase} ${btnNeutral} h-9 w-9 p-0`}
              aria-label="Close"
            >
              <FaTimes className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-5 space-y-5 max-h-[68vh] overflow-y-auto">
            {/* Prompt */}
            <div>
              <label
                htmlFor="ai-image-prompt"
                className="mb-2 block text-xs font-semibold text-slate-900"
              >
                Prompt
              </label>
              <div className="relative">
                <textarea
                  id="ai-image-prompt"
                  rows={8}
                  value={prompt}
                  onChange={(e) => {
                    setIsError(false);
                    setPrompt(e.target.value);
                  }}
                  placeholder='e.g., A golden sunrise over the Serengeti with distant acacia trees, warm tones, cinematic lighting'
                  className={`w-full rounded-xl border text-xs outline-none transition placeholder:text-slate-400 p-4 ${isError
                      ? "border-red-600 focus:border-red-600 focus:ring-2 focus:ring-red-500"
                      : "border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    }`}
                />
                <div className="pointer-events-none absolute bottom-2 right-3 text-[11px] text-slate-400">
                  {prompt.length} chars
                </div>
              </div>
              {isError ? (
                <p className="mt-1 text-[11px] font-medium text-red-600">
                  Please enter a prompt before generating.
                </p>
              ) : null}
              <p className="mt-2 text-[11px] text-slate-500">
                Tip: include subject, setting, mood, and style (e.g., “wide angle”, “soft light”).
              </p>
            </div>

            {/* Generate */}
            <div className="flex items-center justify-between gap-3">
              <button
                disabled={loading}
                onClick={sendPrompt}
                className={`${btnBase} ${btnPrimary}`}
              >
                {loading ? <AiOutlineLoading className="h-4 w-4 animate-spin" /> : null}
                <span>{loading ? "Generating…" : "Generate Image"}</span>
              </button>
              {allGeneratedImages?.length > 0 ? (
                <span className="text-[11px] text-slate-500">
                  Click an image below to select it.
                </span>
              ) : null}
            </div>

            {/* Gallery */}
            <div className="rounded-xl border border-slate-200 p-3">
              <p className="mb-2 text-xs font-semibold text-slate-900">Generated Results</p>
              {allGeneratedImages?.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {allGeneratedImages.map((image, key) => {
                    const active = selected === image.image_id;
                    return (
                      <button
                        type="button"
                        key={key}
                        onClick={() => {
                          setSelected(image.image_id);
                          setThumbnail(image.image_url);
                          setThumbnailId(image.image_id);
                        }}
                        className={`group relative aspect-square w-full overflow-hidden rounded-xl border transition ${active
                            ? "border-blue-600 ring-2 ring-blue-500"
                            : "border-slate-200 hover:border-slate-300"
                          }`}
                      >
                        <img
                          src={image?.image_url}
                          alt={image.image_id}
                          className="h-full w-full object-cover"
                        />
                        <div
                          className={`pointer-events-none absolute inset-0 bg-blue-500/0 transition group-hover:bg-blue-500/5 ${active ? "bg-blue-500/10" : ""
                            }`}
                        />
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="py-6 text-center text-[11px] font-semibold uppercase text-slate-500">
                  Your generated images will appear here
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t bg-white">
            <button
              type="button"
              onClick={() => setShowImageModal(false)}
              className={`${btnBase} ${btnNeutral}`}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={loading || !selected}
              onClick={() => setShowImageModal(false)}
              className={`${btnBase} ${btnPrimary}`}
            >
              Insert Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateImage;
